import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { MapContext } from './mapContext'
import { LatLngLiteral } from '../map/mapPosition'
import { Point, FeatureCollection, Feature, MultiPolygon, Polygon } from 'geojson'
import serial from '~/prix/utils/serial'
import mapboxgl from 'mapbox-gl'
import { formatCompactNumber } from '~/prix'
import { cellsToMultiPolygon } from 'h3-js'
import bbox from '@turf/bbox'
import debounce from 'debounce-promise'

type ChartClusterProps = {
  items: {
    position: LatLngLiteral
    id?: string | number
    color?: string
    type?: string
    label?: string
    description?: string
    weight?: number
    h3?: string
    value?: number
  }[]
  showClusterCount?: boolean
  showPopup?: boolean
  onClusterClick?: (clusterId: number, coordinates: [number, number], zoom?: number) => void
  onItemClick?: (
    item: {
      position: LatLngLiteral
      id?: string | number
      color?: string
      label?: string
    },
    index: number,
  ) => void
  clusterRadius?: number
  clusterMaxZoom?: number
  delimitation?: Polygon | MultiPolygon | undefined
  fontFamily?: string
}

const ids = serial()

function createDonutChart(
  props: {
    total: number
    items: {
      color: string
      count: number
      type: string
    }[]
  },
  showClusterCount: boolean,
  fontFamily: string,
): HTMLElement {
  const { total, items } = props
  const totalTotal = items.map(item => item.count || 0).reduce((a, b) => a + b, 0)

  const fontSize = totalTotal >= 1000 ? 19 : totalTotal >= 100 ? 19 : totalTotal >= 10 ? 17 : 16
  const r = totalTotal >= 1000 ? 50 : totalTotal >= 100 ? 32 : totalTotal >= 10 ? 24 : 18
  const r0 = Math.round(r * 0.6)
  const w = r * 2

  // Usa as contagens reais de cada cor
  const colors = items.map(item => item.color)
  const counts = items.map(item => item.count)

  const offsets: number[] = []
  let offsetTotal = 0
  for (const count of counts) {
    offsets.push(offsetTotal)
    offsetTotal += count
  }

  let html = `<div>
    <svg width="${w}" height="${w}" viewBox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px ${fontFamily}; display: block">`

  function donutSegment(start: number, end: number, r: number, r0: number, color: string): string {
    if (end - start === 1) end -= 0.00001
    const a0 = 2 * Math.PI * (start - 0.25)
    const a1 = 2 * Math.PI * (end - 0.25)
    const x0 = Math.cos(a0)
    const y0 = Math.sin(a0)
    const x1 = Math.cos(a1)
    const y1 = Math.sin(a1)
    const largeArc = end - start > 0.5 ? 1 : 0

    return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${
      r + r * y0
    } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${
      r + r0 * x1
    } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${
      r + r0 * y0
    }" fill="${color}" />`
  }

  for (let i = 0; i < counts.length; i++) {
    if (counts[i] > 0) {
      html += donutSegment(
        offsets[i] / totalTotal,
        (offsets[i] + counts[i]) / totalTotal,
        r,
        r0,
        colors[i],
      )
    }
  }

  html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="white" />
    ${
      showClusterCount
        ? `<text dominant-baseline="central" transform="translate(${r}, ${r})">
      ${formatCompactNumber(totalTotal)}
    </text>`
        : ''
    }
    </svg>
    </div>`

  const el = document.createElement('div')
  el.innerHTML = html

  const chartElement = el.firstChild as HTMLElement

  return chartElement
}

function createClusterPopup(
  total: number,
  items: {
    color: string
    count: number
    type: string
  }[],
  fontFamily: string,
): HTMLElement {
  const totalTotal = items.map(item => item.count || 0).reduce((a, b) => a + b, 0)
  const tooltip = document.createElement('div')
  tooltip.style.lineHeight = '1.5'
  tooltip.style.whiteSpace = 'nowrap'
  tooltip.style.fontFamily = fontFamily

  const titleDiv = document.createElement('div')
  titleDiv.style.fontWeight = 'bold'
  titleDiv.style.marginBottom = '4px'
  titleDiv.textContent = `Total: ${totalTotal.toLocaleString()}`
  tooltip.appendChild(titleDiv)

  items.forEach(item => {
    const percentage = ((item.count / totalTotal) * 100).toFixed(1)

    const itemDiv = document.createElement('div')
    itemDiv.style.display = 'flex'
    itemDiv.style.alignItems = 'center'
    itemDiv.style.gap = '6px'

    const colorBox = document.createElement('div')
    colorBox.style.width = '12px'
    colorBox.style.height = '12px'
    colorBox.style.backgroundColor = item.color
    colorBox.style.borderRadius = '2px'

    const textSpan = document.createElement('span')
    textSpan.textContent = `${item.type}: ${item.count.toLocaleString()} - ${percentage}%`

    itemDiv.appendChild(colorBox)
    itemDiv.appendChild(textSpan)
    tooltip.appendChild(itemDiv)
  })

  return tooltip
}

const ChartCluster = ({
  items,
  onClusterClick,
  onItemClick,
  showClusterCount = true,
  showPopup = true,
  clusterRadius = 50,
  clusterMaxZoom = 14,
  delimitation,
  fontFamily = "'Figtree', Arial, sans-serif",
}: ChartClusterProps) => {
  const mapContext = useContext(MapContext)
  const componentIdRef = useRef(ids.next())
  const markersRef = useRef<{ [key: string]: mapboxgl.Marker }>({})
  const markersOnScreenRef = useRef<{ [key: string]: mapboxgl.Marker }>({})
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const clusterPopupsRef = useRef<{ [key: string]: mapboxgl.Popup }>({})
  const itemsRef = useRef(items)
  const onItemClickRef = useRef(onItemClick)
  const onClusterClickRef = useRef(onClusterClick)
  const showPopupRef = useRef(showPopup)
  const showClusterCountRef = useRef(showClusterCount)
  const fontFamilyRef = useRef(fontFamily)

  if (!mapContext) {
    throw new Error('ChartCluster must be used within a MapBox map')
  }

  const id = componentIdRef.current
  const sourceId = `chartcluster-data-${id}`
  const unclusteredLayerId = `chartcluster-unclustered-${id}`

  const { map } = mapContext

  const debouncedOnClickCluster = debounce((clusterId: number, coordinates: [number, number]) => {
    const zoom = map?.getZoom() || 0
    onClusterClickRef.current?.(clusterId, coordinates, zoom)
  }, 350)

  // Extrai cores únicas para criar propriedades de cluster
  const uniqueColors = useMemo(
    () => Array.from(new Set(items.map(item => item.color || '#11b4da'))).sort(),
    [items],
  )

  // Cria mapeamento de cor para type
  const colorToType = useMemo(() => {
    const mapping: { [key: string]: string } = {}
    items.forEach(item => {
      const color = item.color || '#11b4da'
      if (!mapping[color] && item.type) {
        mapping[color] = item.type
      }
    })
    return mapping
  }, [items])

  // Cria clusterProperties para cada cor
  const clusterProperties: { [key: string]: any } = useMemo(() => {
    const properties: { [key: string]: any } = {
      sum: ['+', ['get', 'value']],
      h3Indexes: [
        ['concat', ['accumulated'], ['get', 'h3Indexes']],
        ['concat', ['get', 'h3'], ','],
      ],
    }

    uniqueColors.forEach(color => {
      const colorKey = `color_${color.replace('#', '')}`
      properties[colorKey] = [
        '+',
        ['case', ['==', ['get', 'color'], color], ['coalesce', ['get', 'weight'], 0], 0],
      ]
    })

    return properties
  }, [uniqueColors])

  const uniqueColorsRef = useRef(uniqueColors)
  const colorToTypeRef = useRef(colorToType)

  useEffect(() => {
    itemsRef.current = items
    onItemClickRef.current = onItemClick
    onClusterClickRef.current = onClusterClick
    showPopupRef.current = showPopup
    showClusterCountRef.current = showClusterCount
    fontFamilyRef.current = fontFamily
    uniqueColorsRef.current = uniqueColors
    colorToTypeRef.current = colorToType
  }, [
    colorToType,
    fontFamily,
    items,
    onClusterClick,
    onItemClick,
    showClusterCount,
    showPopup,
    uniqueColors,
  ])

  const cellToFeature = useCallback(
    (h3: string[], value: number, numerator?: number, denominator?: number) => {
      try {
        const h3Filtered = [...new Set(h3)]
        const polygonCoords = cellsToMultiPolygon(h3Filtered, true)
        const feature: Feature<
          Polygon | MultiPolygon,
          { h3: string; value: number; numerator?: number; denominator?: number }
        > = {
          type: 'Feature',
          geometry: {
            type: 'MultiPolygon',
            coordinates: polygonCoords,
          },
          properties: {
            h3: h3Filtered.join(','),
            value: value,
            numerator: numerator,
            denominator: denominator,
          },
        }
        return feature
      } catch (e) {
        console.error('Error converting H3 cells to polygon:', e)
        return null
      }
    },
    [],
  )

  const clusterData: FeatureCollection<Point> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: items.map((item, index) => ({
        type: 'Feature',
        properties: {
          color: item.color || '#11b4da',
          label: item.label || '',
          id: item.id ?? '',
          type: item.type || '',
          itemIndex: index,
          weight: item.weight,
          h3: item.h3 || '',
          value: item.value || 0,
        },
        geometry: {
          type: 'Point',
          coordinates: [item.position.lng, item.position.lat],
        },
      })),
    }),
    [items],
  )

  const updateMarkers = useCallback(
    (sourceId: string) => {
      const newMarkers: { [key: string]: mapboxgl.Marker } = {}
      const features = map?.querySourceFeatures(sourceId)

      if (!features) return

      const clustersById = new Map<string, mapboxgl.MapboxGeoJSONFeature>()

      for (const feature of features) {
        const props = feature.properties
        if (
          !props ||
          !props.cluster ||
          props.cluster_id === undefined ||
          props.cluster_id === null
        ) {
          continue
        }
        const clusterId = String(props.cluster_id)
        if (!clustersById.has(clusterId)) {
          clustersById.set(clusterId, feature)
        }
      }

      // Cria markers HTML para cada cluster
      for (const [clusterId, feature] of clustersById.entries()) {
        const coords = feature.geometry.type === 'Point' ? feature.geometry.coordinates : null
        const props = feature.properties

        if (!coords || !props || !props.cluster) continue

        const id = clusterId
        let marker = markersRef.current[id]

        //console.log('Processing cluster:', { id, coords, props })

        if (!marker) {
          // Extrai as contagens de cores das propriedades do cluster
          const clusterItems: { color: string; count: number; type: string }[] = []
          uniqueColorsRef.current.forEach(color => {
            const colorKey = `color_${color.replace('#', '')}`
            const count = props[colorKey] || 0
            if (count > 0) {
              clusterItems.push({
                color: color,
                count: count,
                type: colorToTypeRef.current[color] || '',
              })
            }
          })

          const el = createDonutChart(
            {
              total: props.point_count,
              items: clusterItems,
            },
            showClusterCountRef.current,
            fontFamilyRef.current,
          )

          el.style.cursor = 'pointer'

          // Adiciona click handler ao cluster
          el.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation()

            const h3 = props.h3Indexes ? String(props.h3Indexes).split(',').filter(Boolean) : []
            const value = Number(props.sum || 0)

            const feature = cellToFeature(h3, value)
            if (feature) {
              const bboxFeature = bbox(feature)
              map?.fitBounds(
                [
                  [bboxFeature[0], bboxFeature[1]],
                  [bboxFeature[2], bboxFeature[3]],
                ],
                { padding: 20, duration: 400 },
              )
            }

            debouncedOnClickCluster(Number(id), coords as [number, number])
          })

          const popupCluster = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            closeOnMove: true,
          })

          const addPopup = () => {
            popupRef.current?.remove()
            popupRef.current = popupCluster
            popupCluster
              ?.setLngLat(coords as [number, number])
              .setOffset(25)
              .setDOMContent(
                createClusterPopup(props.point_count, clusterItems, fontFamilyRef.current),
              )
              .addTo(map!)
          }

          const removePopup = () => {
            popupCluster?.remove()
            if (popupRef.current === popupCluster) {
              popupRef.current = null
            }
          }

          marker = markersRef.current[id] = new mapboxgl.Marker({
            element: el,
          }).setLngLat(coords as [number, number])
          clusterPopupsRef.current[id] = popupCluster

          marker.getElement().addEventListener('mouseenter', addPopup)
          marker.getElement().addEventListener('mouseleave', removePopup)

          marker.on('mouseleave', () => {
            marker.getElement().dispatchEvent(new Event('mouseleave'))
          })
        } else {
          marker.setLngLat(coords as [number, number])
        }

        newMarkers[id] = marker

        if (!markersOnScreenRef.current[id]) {
          marker.addTo(map!)
        }
      }

      for (const id in markersOnScreenRef.current) {
        if (!newMarkers[id]) {
          markersOnScreenRef.current[id].fire('mouseleave')
          clusterPopupsRef.current[id]?.remove()
          if (popupRef.current === clusterPopupsRef.current[id]) {
            popupRef.current = null
          }
          delete clusterPopupsRef.current[id]
          markersOnScreenRef.current[id].remove()
        }
      }

      markersOnScreenRef.current = newMarkers
    },
    [cellToFeature, map],
  )

  useEffect(() => {
    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    // Adiciona a fonte com clustering e propriedades agregadas
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: true,
      clusterMaxZoom: clusterMaxZoom,
      clusterRadius: clusterRadius,
      clusterProperties: clusterProperties,
    })

    // Adiciona layer para pontos não clusterizados
    map.addLayer({
      id: unclusteredLayerId,
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })

    // Atualiza markers quando o mapa renderiza
    const onRender = () => {
      if (!map.isSourceLoaded(sourceId)) return
      updateMarkers(sourceId)
    }

    map.on('render', onRender)

    // Click handler para pontos individuais
    const onUnclusteredClick = (e: mapboxgl.MapMouseEvent) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [unclusteredLayerId],
      })

      if (features.length > 0) {
        const feature = features[0]
        const itemIndex = feature.properties?.itemIndex
        const currentItems = itemsRef.current
        if (itemIndex !== undefined && currentItems[itemIndex]) {
          if (onItemClickRef.current) {
            onItemClickRef.current(currentItems[itemIndex], itemIndex)

            if (!!currentItems[itemIndex].id) {
              return
            }
          }

          if (showPopupRef.current) {
            const popupElement = document.createElement('div')
            popupElement.style.padding = '0 25px 0 0'
            popupElement.style.fontFamily = fontFamilyRef.current

            const titleElement = document.createElement('p')
            titleElement.style.lineHeight = '1'
            titleElement.style.marginTop = '0'
            titleElement.style.fontWeight = 'bold'
            titleElement.style.paddingRight = '15px'
            titleElement.textContent = currentItems[itemIndex].label || 'Item'
            popupElement.appendChild(titleElement)

            const descriptionLines = currentItems[itemIndex].description?.split('\n')

            if (descriptionLines) {
              for (const descriptionLine of descriptionLines) {
                const [key, value] = descriptionLine.split(': ')
                const descriptionElement = document.createElement('p')
                descriptionElement.style.marginTop = '5px'
                descriptionElement.style.lineHeight = '1.2'
                descriptionElement.innerHTML = `<strong>${key}:</strong> ${value || ''}`

                if (descriptionLine === descriptionLines[descriptionLines.length - 1]) {
                  descriptionElement.style.marginBottom = '0'
                }

                popupElement.appendChild(descriptionElement)
              }
            }

            new mapboxgl.Popup()
              .setLngLat(currentItems[itemIndex].position)
              .setOffset(10)
              .setDOMContent(popupElement)
              .addTo(map)
          }
        }
      }
    }

    const onUnclusteredMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const onUnclusteredMouseLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', unclusteredLayerId, onUnclusteredClick)
    map.on('mouseenter', unclusteredLayerId, onUnclusteredMouseEnter)
    map.on('mouseleave', unclusteredLayerId, onUnclusteredMouseLeave)

    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      // Remove event listeners
      map.off('render', onRender)
      map.off('click', unclusteredLayerId, onUnclusteredClick)
      map.off('mouseenter', unclusteredLayerId, onUnclusteredMouseEnter)
      map.off('mouseleave', unclusteredLayerId, onUnclusteredMouseLeave)

      // Remove todos os markers
      for (const id in markersOnScreenRef.current) {
        clusterPopupsRef.current[id]?.remove()
        markersOnScreenRef.current[id].remove()
      }
      popupRef.current?.remove()
      popupRef.current = null
      markersRef.current = {}
      markersOnScreenRef.current = {}
      clusterPopupsRef.current = {}

      // Remove layers e source
      if (map.getLayer(unclusteredLayerId)) {
        map.removeLayer(unclusteredLayerId)
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    }
  }, [
    map,
    clusterMaxZoom,
    clusterProperties,
    clusterRadius,
    sourceId,
    unclusteredLayerId,
    updateMarkers,
  ])

  useEffect(() => {
    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined
    if (!source) {
      return
    }

    source.setData(clusterData)
  }, [clusterData, map, sourceId])

  return null
}

export default memo(ChartCluster, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.items) === JSON.stringify(nextProps.items) &&
    prevProps.showClusterCount === nextProps.showClusterCount &&
    prevProps.showPopup === nextProps.showPopup &&
    prevProps.clusterRadius === nextProps.clusterRadius &&
    prevProps.clusterMaxZoom === nextProps.clusterMaxZoom &&
    prevProps.fontFamily === nextProps.fontFamily
  )
})
